import {useState} from "react";
import {App as AntdApp, Tooltip} from "antd";
import className from "classnames/bind";
import {FontAwesomeIcon, PageHeader, SearchBar, Button} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useAnalyzeSeoMutation} from "~/reduxs/api/seoApiSlice";
import SeoResult from "../components/SeoResult";
import style from "../style/SeoCheck.module.scss";

const cn = className.bind(style);

/**
 * Trang công cụ chấm điểm SEO: nhập domain → BE craw + chấm → hiển thị kết quả.
 */
function SeoCheck() {

	const {notification} = AntdApp.useApp();

	const [analyzeSeo, {isLoading}] = useAnalyzeSeoMutation();

	const [result, setResult] = useState(null);
	const [keyword, setKeyword] = useState('');

	const handleAnalyze = async (domain) => {
		try {
			const data = await analyzeSeo({domain, keyword: keyword.trim()}).unwrap();
			setResult(data);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Chấm điểm thất bại')});
		}
	};

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-magnifying-glass-chart"
				title="Chấm điểm SEO"
				subtitle="Nhập domain để phân tích và chấm điểm SEO on-page"
				actions={
					<Button to="/seo/history" white
						leftIcon={<FontAwesomeIcon icon="fa-light fa-clock-rotate-left" />}>
						Lịch sử
					</Button>
				}
			/>

			<SearchBar
				icon="fa-light fa-globe"
				placeholder="Nhập domain, ví dụ: sikido.vn"
				buttonText="Chấm điểm"
				buttonIcon="fa-light fa-magnifying-glass-chart"
				loading={isLoading}
				onSubmit={handleAnalyze}
			/>

			<div className={cn('keyword-field')}>
				<label>
					<FontAwesomeIcon icon="fa-light fa-key" />
					<span>Từ khoá mục tiêu <em>(tuỳ chọn)</em></span>
					<Tooltip title="Nhập từ khoá để chấm thêm nhóm tiêu chí On-page theo từ khoá (từ khoá có trong title, description, H1, nội dung, URL…). Chỉ áp dụng khi chấm 1 TRANG cụ thể — hãy nhập URL đầy đủ thay vì domain.">
						<span style={{cursor: 'help', color: '#9ca3af'}}>
							<FontAwesomeIcon icon="fa-light fa-circle-question" />
						</span>
					</Tooltip>
				</label>
				<input
					type="text"
					value={keyword}
					disabled={isLoading}
					placeholder="Ví dụ: công cụ SEO — để chấm On-page theo từ khoá"
					onChange={(e) => setKeyword(e.target.value)}
				/>
			</div>

			{isLoading && (
				<div className={cn('loading')}>
					<span className="app-spinner" />
					<p>Đang craw và phân tích website, vui lòng đợi…</p>
				</div>
			)}

			{!isLoading && result && (
				<div className={cn('result-domain')}>
					<FontAwesomeIcon icon="fa-light fa-globe" /> Kết quả cho <strong>{result.domain}</strong>
					{result.meta?.keyword && <> — từ khoá: <strong>{result.meta.keyword}</strong></>}
				</div>
			)}

			{/* Lần đầu vào trang (chưa chấm lần nào) — hướng dẫn thay vì cột kết quả trống trơn. */}
			{!isLoading && !result && (
				<div className={cn('first-run')}>
					<FontAwesomeIcon icon="fa-light fa-magnifying-glass-chart" />
					<h3>Nhập domain hoặc URL để bắt đầu</h3>
					<p>Công cụ sẽ tải trang, phân tích và chấm điểm SEO on-page. Bạn sẽ nhận được:</p>
					<ul>
						<li>Điểm tổng 0–100 + xếp hạng A–F, chia theo 7 nhóm tiêu chí (Kỹ thuật, Meta, Mạng xã hội, Nội dung, Hình ảnh, Cấu trúc, Bảo mật).</li>
						<li>Danh sách tiêu chí đạt / chưa đạt kèm hướng khắc phục cụ thể.</li>
						<li>Xem trước trang trên Google (SERP), Facebook và X.</li>
						<li>Nhập thêm <strong>từ khoá mục tiêu</strong> + URL 1 trang cụ thể để chấm On-page theo từ khoá.</li>
					</ul>
				</div>
			)}

			{!isLoading && <SeoResult result={result} />}
		</div>
	);
}

export default SeoCheck;
